/* eslint-disable import/no-cycle */
import { A11yModule } from '@angular/cdk/a11y'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { combineLatest, debounceTime, withLatestFrom } from 'rxjs'

import {
  FlxModalStyleDirective,
  FlxThemeModule,
  FlxWrapperStyleDirective,
} from '@flowx/angular-theme'
import {
  FlxButtonComponent,
  FlxIconModule,
  FlxKeyboardDirective,
  FlxModalComponent,
  FlxShowModalDirective,
} from '@flowx/angular-ui-toolkit'
import {
  ModalTemplateConfig,
  allActiveNodesAndContexts$,
  closeModal,
  dismissProcess,
  fonts,
  getActiveUiFlowSessionId,
  getCurrentRootTemplateConfig$,
  getOpenModal,
  isRootTemplateConfig,
  navigationStack,
  templateConfigsByParentTemplateId$,
  theme,
  triggerDisplayEvent,
} from '@flowx/core-sdk'
import { ModalHeaderStyles, ThemeComponent, applyModalHeaderCustomStyles } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxTreeNodeComponent } from '../../renderer/tree-node/tree-node.component'
import { FlxActionService } from '../../services/flx-action.service'
import { emitScreenAnalyticsOnDisplay } from '../../utils/screen-analytics.utils'
import { getDynamicPropSignal, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-modal-wrapper',
  imports: [
    FlxShowModalDirective,
    FlxTreeNodeComponent,
    FlxIconModule,
    FlxModalComponent,
    FlxLocalizePipe,
    FlxButtonComponent,
    FlxThemeModule,
    FlxWrapperStyleDirective,
    FlxModalStyleDirective,
    A11yModule,
    FlxKeyboardDirective,
  ],
  templateUrl: './modal-wrapper.component.html',
  styleUrl: './modal-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxModalWrapperComponent implements OnInit {
  #injector = inject(Injector)
  #actionService = inject(FlxActionService)
  #destroyRef = inject(DestroyRef)

  templateConfig = input.required<ModalTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  // dynamic props
  title: Signal<string>
  subtitle: Signal<string>
  dismissTitle: Signal<string>
  dismissMessage: Signal<string>
  dismissCancelLabel: Signal<string>
  dismissConfirmLabel: Signal<string>

  showModal = signal(true)
  dismissBottomSheetHidden = signal(true)
  headerClass = signal<string>('')
  closeIconPath = computed(() => getThemeIcon(ThemeComponent.ModalHeader, 'closeIcon'))

  readonly DISMISS_LABEL = '@@sys_a11y_modal_close'

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const { modalDismissAlert, title, subtitle } = this.flowxProps()
      const { title: dismissTitle, message, cancelLabel, confirmLabel } = modalDismissAlert || {}
      const { processInstanceUuid, id, context } = this.templateConfig()

      this.title = getDynamicPropSignal(title || '', this.templateConfig())
      this.subtitle = getDynamicPropSignal(subtitle || '', this.templateConfig())
      this.dismissTitle = getDynamicPropSignal(dismissTitle, this.templateConfig())
      this.dismissMessage = getDynamicPropSignal(message, this.templateConfig())
      this.dismissCancelLabel = getDynamicPropSignal(cancelLabel, this.templateConfig())
      this.dismissConfirmLabel = getDynamicPropSignal(confirmLabel, this.templateConfig())

      effect((onCleanup) => {
        const style = this.displayOptions().style as unknown as
          Partial<ModalHeaderStyles> | undefined
        if (!style) {
          this.headerClass.set('')
          return
        }
        let cancelled = false
        onCleanup(() => {
          cancelled = true
        })
        applyModalHeaderCustomStyles(style, theme(), fonts()).then((className) => {
          if (!cancelled) {
            this.headerClass.set(className)
          }
        })
      })

      combineLatest([
        allActiveNodesAndContexts$(processInstanceUuid).pipe(
          withLatestFrom(templateConfigsByParentTemplateId$(processInstanceUuid, id))
        ),
        getCurrentRootTemplateConfig$,
      ])
        .pipe(debounceTime(0), takeUntilDestroyed())
        .subscribe(([[activeNodes, childTcs], currentRootTemplateConfig]) => {
          this.showModal.set(
            (currentRootTemplateConfig?.id === id &&
              currentRootTemplateConfig?.context === context) ||
              childTcs.some((tc) =>
                activeNodes.some(
                  (node) =>
                    node.currentNodeId === tc.nodeDefinitionId && node.currentContext === tc.context
                )
              )
          )
        })

      this.#actionService.runOnLoadEvent(
        this.templateConfig(),
        this.#destroyRef,
        () => getActiveUiFlowSessionId() !== null
      )
      // Modal has no onDisplay event of its own, but we still register the DISPLAY pathway
      // so AI_TRIGGER actions (now part of the DISPLAY list) fire after onLoad completes.
      this.#actionService.runOnDisplayEvent(
        this.templateConfig(),
        this.#destroyRef,
        () => getActiveUiFlowSessionId() !== null
      )

      // Navigation areas are screen roots in UI flows and emit the same SCREEN event a user
      // task root container does. Armed after onLoad so it waits on that load, not past it.
      emitScreenAnalyticsOnDisplay(this.templateConfig(), this.flowxProps(), this.#destroyRef)
    })
  }

  backdropClicked(): void {
    if (this.flowxProps().canDismissModal && this.flowxProps().closeOnBackdropClick) {
      this.closeModal()
    }
  }

  showDismissBottomSheet(): void {
    this.dismissBottomSheetHidden.set(false)
  }

  hideDismissBottomSheet(): void {
    this.dismissBottomSheetHidden.set(true)
  }

  dismissModal(): void {
    this.hideDismissBottomSheet()
    this.showModal.set(false)

    if (isRootTemplateConfig(this.templateConfig())) {
      // axios interceptor surfaces the error toast; swallow the rejection so it
      // doesn't bubble as an unhandled promise rejection while the modal closes.
      dismissProcess(this.templateConfig().processInstanceUuid).catch(() => {})
    }

    const wasOpen = getOpenModal() !== null

    // Pop the modal's navigation stack entry via history.back().
    // Only in UI flow context — process-renderer modals don't use the navigation stack.
    if (getActiveUiFlowSessionId() !== null && navigationStack.currentEntry?.ephemeral?.modalId) {
      navigationStack.pop()
    }

    closeModal()

    if (wasOpen) {
      triggerDisplayEvent('modal-dismiss')
    }
  }

  closeModal(): void {
    if (this.flowxProps().showDismissConfirmationAlert) {
      this.showDismissBottomSheet()
    } else {
      this.dismissModal()
    }
  }

  confirmDismiss(): void {
    this.dismissModal()
  }

  cancelDismiss(): void {
    this.hideDismissBottomSheet()
  }

  closeButtonClicked(): void {
    if (this.flowxProps().canDismissModal) {
      this.closeModal()
    }
  }
}
